/**
 * Patron (docs/ideas/loom.md, business decisions): the free first chapter, then Patron by subscription, with packs
 * for heavy months. Shared by the worker (gates, Stripe) and the page (what the offer says).
 */

/** Free: the first 40 turns, counted across all of a player's stories. */
export const FREE_TURNS = 40;
/** At this many free turns (or when the first chapter ends, if sooner) the story dreams then and there. */
export const FIRST_DREAM_AT = 25;
/** Free turns from one network (an IP address, or an IPv6 /64) per UTC day, across every player on it (owner, 2026-10-17;
 *  240 from 2026-10-21, for offices and campuses sharing one address). */
export const FREE_TURNS_PER_NETWORK = 240;
/** "N free turns left" shows from here down. */
export const FREE_WARN_AT = 10;
/** Free: the first nightly dream (and its plate). */
export const FREE_DREAMS = 1;
/** Patron: a monthly allocation (about 20 a day), reset on the 1st (UTC). Owner, 2026-10-18: 600 on every plan, after
 *  measuring about $0.010 a turn (900 lost money on a Patron who used them all). */
export const PATRON_TURNS_PER_MONTH = 600;
/** A Patron's monthly allocation on this plan (the same on every plan today). */
export const turnsFor = (_plan: Plan): number => PATRON_TURNS_PER_MONTH;
/** The turns a plan's payment covers: a month's allocation, or twelve for a yearly plan. */
export const turnsPerPayment = (plan: Plan): number => turnsFor(plan) * (PLANS[plan].per === "year" ? 12 : 1);
/** The founding price goes to the first this-many Patrons. */
export const FOUNDING_SEATS = 200;
/** ...for this many monthly payments; then the Patronage continues as Patron, monthly (owner, 2026-10-18). */
export const FOUNDING_MONTHS = 12;
/** When a founding Patronage that began at `start` moves to the regular price: twelve calendar months on. */
export function foundingEnds(start: number): number {
  const d = new Date(start);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + FOUNDING_MONTHS, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes());
}

export type Plan = "monthly" | "yearly" | "founding";
/** Owner, 2026-10-18: yearly $79.99 (was $59.99); the founding yearly plan is gone. */
export const PLANS: Record<Plan, { usd: number; per: "month" | "year"; label: string }> = {
  monthly: { usd: 7.99, per: "month", label: "Patron, monthly" },
  yearly: { usd: 79.99, per: "year", label: "Patron, yearly" },
  founding: { usd: 4.99, per: "month", label: "Founding Patron, monthly" },
};

export type PackId = "pack3" | "pack5" | "pack10" | "pack20" | "packChapter";
/** One-time packs: turns that carry over until used. Three sizes for Patrons who run out, and "finish this chapter"
 *  for players who aren't Patrons (owner, 2026-10-17). */
/*  Owner, 2026-10-18: 2¢ a turn (about 40% margin once used, at ~$0.010 a turn), dearer than a Patron's 1.33¢ so
 *  regular players are better off subscribing; fixed sizes from $3 (Stripe's 30¢ fee eats a $2 sale). */
export const PACKS: Record<PackId, { usd: number; turns: number }> = {
  pack3: { usd: 3, turns: 150 },
  pack5: { usd: 5, turns: 250 },
  pack10: { usd: 10, turns: 500 },
  pack20: { usd: 20, turns: 1000 },
  packChapter: { usd: 2.99, turns: 60 },
};
/** The packs offered to a Patron who ran out (the chapter pack is for everyone else). */
export const PATRON_PACKS: PackId[] = ["pack3", "pack5", "pack10", "pack20"];
/** At most this many unused pack turns held at once: a pack that would go past it can't be bought (Terms §6.5). */
export const PACK_TURNS_MAX = 2000;
/** Whether a player holding `held` pack turns may buy pack `id`. */
export const packFits = (id: PackId, held: number) => held + PACKS[id].turns <= PACK_TURNS_MAX;

/** What the page shows about the player's allowance (GET /api/patron). */
export interface PatronView {
  signedIn: boolean;
  /** `pass`: a friend pass from the owner (no Stripe subscription: nothing to manage or cancel). */
  patron: { plan: Plan; status: string; renews: number | null; cancelsAtEnd: boolean; pass?: boolean } | null;
  free: { used: number; limit: number };
  month: { used: number; limit: number; resets: number } | null;
  packTurns: number;
  /** Dreams dreamt while not a Patron (the first one comes in the session: POST /first-dream). */
  dreamsUsed: number;
  foundingLeft: number;
  /** Payments are switched on here (Stripe keys and prices are set). */
  open: boolean;
}

/** `t` moved by `k` months, on the same day and time; a day the month doesn't have becomes its last (Jan 31 → Feb 28),
 *  as Stripe bills. */
export function addMonths(t: number, k: number): number {
  const d = new Date(t);
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + k;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Date.UTC(y, m, Math.min(d.getUTCDate(), last), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
}
/**
 * A Patron's allowance month: it begins on the day their Patronage began and renews on that day each month (with the
 * monthly bill; a yearly Patron's 600 a month the same way), not on the 1st (owner, 2026-10-18: "renews on the 18th,
 * but the turns start afresh on October 31" made no sense, and a Patron who joined on the 28th got two months'
 * turns in four days). The key names the period in the usage doc.
 */
export function allowancePeriod(anchor: number, now: number): { key: string; start: number; end: number } {
  const a = new Date(anchor), n = new Date(now);
  let k = Math.max(0, (n.getUTCFullYear() - a.getUTCFullYear()) * 12 + n.getUTCMonth() - a.getUTCMonth());
  while (k > 0 && addMonths(anchor, k) > now) k--;
  while (addMonths(anchor, k + 1) <= now) k++;
  const start = addMonths(anchor, k);
  return { key: `p${new Date(start).toISOString().slice(0, 16)}`, start, end: addMonths(anchor, k + 1) };
}

/** The UTC month a turn counts in ("2026-10"), and when the next one begins (Patrons from before their start was kept). */
export const monthOf = (now: number) => new Date(now).toISOString().slice(0, 7);
export function nextMonth(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}
